import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDevice } from '@/contexts/DeviceContext';
import { config } from '@/config/environment';

// Helper to get serial from header injection (window variable or meta tag)
const getSerialFromHeader = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  
  // Check window variable (injected by server/native app)
  const windowSerial = (window as any).__DEVICE_SERIAL__;
  if (windowSerial) return windowSerial;
  
  // Check meta tag (injected by server/proxy)
  const metaTag = document.querySelector('meta[name="x-device-serial"]');
  if (metaTag) {
    const content = metaTag.getAttribute('content');
    if (content) return content;
  }
  
  return undefined;
};

export const useEffectiveSerial = () => {
  const { selectedDevice } = useDevice();
  const params = useParams();
  const [searchParams] = useSearchParams();

  // Validate serial - filter out route paths and invalid values
  const isValidSerial = (value: string | undefined): boolean => {
    if (!value) return false;
    const invalidSerials = ['updates', 'settings', 'support', 'requests', 'general', 'device', 'device-selector'];
    return !invalidSerials.includes(value.toLowerCase());
  };

  const serialFromHeader = getSerialFromHeader();
  const serialFromQuery = searchParams.get('cx__serial') || undefined;
  const serialFromPath = params.deviceId || undefined;
  const serialFromContext = selectedDevice?.serial || undefined;
  const serialFromStorage = typeof window !== 'undefined' ? localStorage.getItem('device_serial') || undefined : undefined;

  const resolved = useMemo(() => {
    // Priority: header > query > path > context > localStorage > default
    const serial = 
      (isValidSerial(serialFromHeader) ? serialFromHeader : undefined) ||
      (isValidSerial(serialFromQuery) ? serialFromQuery : undefined) ||
      (isValidSerial(serialFromPath) ? serialFromPath : undefined) ||
      (isValidSerial(serialFromContext) ? serialFromContext : undefined) ||
      (isValidSerial(serialFromStorage) ? serialFromStorage : undefined) ||
      config.app.defaultDeviceSerial;

    const source =
      (isValidSerial(serialFromHeader) && 'header') ||
      (isValidSerial(serialFromQuery) && 'query') ||
      (isValidSerial(serialFromPath) && 'path') ||
      (isValidSerial(serialFromContext) && 'context') ||
      (isValidSerial(serialFromStorage) && 'localStorage') ||
      'default';

    return { serial, source };
  }, [serialFromHeader, serialFromQuery, serialFromPath, serialFromContext, serialFromStorage]);

  useEffect(() => {
    if (typeof window !== 'undefined' && resolved.serial && isValidSerial(resolved.serial)) {
      localStorage.setItem('device_serial', resolved.serial);
    }
  }, [resolved.serial]);

  return resolved;
};
